import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import updateBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/UpdateBillTo";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import BillToSelector, { BillToSelectorStyles } from "@insite/content-library/Components/BillToSelector";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import { AddressesPageContext } from "@insite/content-library/Pages/AddressesPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Info from "@insite/mobius/Icons/Info";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden/VisuallyHidden";
import WORAddressInfoDisplay from "@tKgpco/Components/WOR/WORAddressInfoDisplay";
import { getWORData } from "@tKgpco/Services/WORService";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import { useWORCheckoutShippingFormContext } from "@tKgpco/Widgets/WOR/WORCheckoutShippingPage";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentBillTo: getCurrentBillToState(state).value,
    countries: getCurrentCountries(state),
    billToAddressFields: getAddressFieldsDataView(state).value?.billToAddressFields,
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    updateBillTo,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentBillingAddressStyles {
    gridContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    addressFormModal?: ModalPresentationProps;
    addressInfoDisplayGridItem?: GridItemProps;
    selectSavedAddressLink?: LinkPresentationProps;
    billTooltip?: TooltipPresentationProps;
    title?: InjectableCss;
    billToSelector?: BillToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    addressBookModalCancelButton?: ButtonPresentationProps;
    headerGridItem?: GridItemProps;
}

export const currentBillingAddressStyles: CurrentBillingAddressStyles = {
    gridContainer: {
        gap: 5,
        css: css`
            padding: 10px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            padding: 15px 0;
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
            font-size: 14px !important;
        `,
    },
    editLinkGridItem: {
        width: 2,
        css: css`
            font-size: 1rem;
            justify-content: flex-end;
        `,
    },
    addressFormModal: { sizeVariant: "medium" },
    addressInfoDisplayGridItem: { width: 12 },
    selectSavedAddressLink: {
        css: css`
            text-transform: capitalize;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            padding: 0px 21px;
            min-height: 40px;
            width: 100%;
            justify-content: center;
            font-size: 14px;
            color: ${COLORS.secondaryBtn};
            font-weight: 600;
            &:hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
                span {
                    color: ${COLORS.secondaryBtn} !important;
                }
            }
        `,
    },
    billTooltip: {
        iconProps: {
            src: Info,
            css: css`
                svg {
                    color: ${COLORS.primary.base};
                    width: 18px;
                    height: 18px;
                }
            `,
        },
        cssOverrides: {
            tooltipContainer: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                left: auto !important;
                                right: -5px !important;
                                &:after {
                                    content: "";
                                    left: auto !important;
                                    right: 11px !important;
                                }
                            `,
                            null,
                            null,
                        ],
                        "max",
                    )}
            `,
        },
    },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    addressBookModalCancelButton: {
        typographyProps: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.bold,
        },
        css: css`
            text-transform: capitalize;
            border-radius: 22px;
            border: 1px solid ${COLORS.secondaryBtn};
            min-width: 120px;
            background: ${COLORS.white};
            color: ${COLORS.secondaryBtn};
            &:hover {
                background: ${COLORS.secondaryBg};
                border: 1px solid ${COLORS.secondaryBtn};
            }
            &:disabled:hover {
                background: ${COLORS.disabled.base};
                border: 1px solid ${COLORS.disabled.base};
            }
        `,
    },
    headerGridItem: {
        width: 12,
        css: css`
            background: #f1f6fb;
            padding: 14px;
            border-bottom: 2px solid #1e75ba;
            border-radius: 8px 8px 0 0;
        `,
    },
    title: {
        css: css`
            font-size: 14px;
            display: flex;
            justify-content: space-between;
            width: 100%;
            align-items: center;
        `,
    },
};

const styles = currentBillingAddressStyles;

const CurrentBillingAddress: React.FunctionComponent<Props> = (props: Props) => {
    const { currentBillTo } = props;

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);

    const { dispatch } = useWORCheckoutShippingFormContext();
    const worData = getWORData();
    useEffect(() => {
        dispatch({ billingAddress: currentBillTo });
    }, [currentBillTo]);

    if (!currentBillTo) {
        return null;
    }

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const editClickHandler = () => {
        setModalIsOpen(true);
    };
    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };
    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: BillToModel) => {
        if (!props.currentBillTo) {
            return;
        }
        props.updateBillTo({
            billTo: {
                ...props.currentBillTo,
                ...address,
            },
            isEdit: false,
        });
        modalCloseHandler();
    };

    const selectAddressClickHandler = () => setIsCustomerSelectorModalOpen(true);

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const selectCustomerHandler = (billTo: BillToModel) => {
        customerSelectorModalCloseHandler();
        // onChange(billTo);
    };

    const editCustomerHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BillToModel) => {
        event.preventDefault();
        // setAddressBeingEdited(customer);
    };

    const { countries, billToAddressFields, customerSettings } = props;

    return (
        <>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.headerGridItem}>
                    <StyledWrapper {...styles.title}>
                        <Typography as="h3" {...styles.headingText}>
                            {translate("Customer Account Address")}
                        </Typography>
                        <Tooltip {...styles.billTooltip} text={siteMessage("WOR_BillTo_message").toString()} />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.headingGridItem}>
                    <Link
                        {...styles.selectSavedAddressLink}
                        type="button"
                        disabled={true}
                        onClick={selectAddressClickHandler}
                        data-test-selector="checkoutShipping_selectSavedAddress"
                    >
                        {translate("Select Saved Address")}
                        <VisuallyHidden>{translate("For Billing Address")}</VisuallyHidden>
                    </Link>
                </GridItem>
                {customerSettings.allowBillToAddressEdit && (
                    <GridItem {...styles.editLinkGridItem}>
                        {countries && (
                            <Link
                                {...styles.editLink}
                                onClick={editClickHandler}
                                data-test-selector="billTo_Edit_Address"
                            >
                                {translate("Edit")}
                            </Link>
                        )}
                        {countries && billToAddressFields && (
                            <Modal
                                headline={translate("Edit Billing Information")}
                                {...styles.addressFormModal}
                                isOpen={modalIsOpen}
                                handleClose={modalCloseHandler}
                            >
                                <CustomerAddressForm
                                    address={currentBillTo}
                                    countries={countries}
                                    addressFieldDisplayCollection={billToAddressFields}
                                    onCancel={formCancelHandler}
                                    onSubmit={formSubmitHandler}
                                />
                            </Modal>
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.addressInfoDisplayGridItem}>
                    <WORAddressInfoDisplay
                        firstName={currentBillTo.firstName}
                        lastName={currentBillTo.lastName}
                        companyName={currentBillTo.companyName}
                        attention={currentBillTo.attention}
                        address1={currentBillTo.address1}
                        address2={currentBillTo.address2}
                        address3={currentBillTo.address3}
                        address4={currentBillTo.address4}
                        city={currentBillTo.city}
                        state={currentBillTo.state ? currentBillTo.state.abbreviation : undefined}
                        postalCode={currentBillTo.postalCode}
                        country={currentBillTo.country ? currentBillTo.country.abbreviation : undefined}
                        phone={currentBillTo.phone}
                        fax={currentBillTo.fax}
                        email={currentBillTo.email}
                        shipAddress3={worData.orderShipto.stAddress3}
                        shipAddress4={worData.orderShipto.stAddress4}
                    />
                </GridItem>
            </GridContainer>
            <Modal
                sizeVariant="medium"
                headline={translate("Address Book")}
                isOpen={isCustomerSelectorModalOpen}
                handleClose={customerSelectorModalCloseHandler}
                data-test-selector="checkoutShipping_addressModal"
            >
                <BillToSelector
                    onSelect={selectCustomerHandler}
                    onEdit={editCustomerHandler}
                    extendedStyles={styles.billToSelector}
                    currentBillTo={currentBillTo}
                />
                <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                    <Button
                        {...styles.addressBookModalCancelButton}
                        onClick={customerSelectorModalCloseHandler}
                        data-test-selector="addressModal_cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentBillingAddress),
    definition: {
        displayName: "WOR Billing Addresses",
        group: "Addresses",
    },
};

export default widgetModule;
